import React, { useState, ReactNode, useEffect } from 'react';
import './Tabs.css';

interface Tab {
  id: string;
  label: string;
  content: ReactNode;
  section?: string;
  sectionLabel?: string;
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
}

const APPROVER_SECTION = 'approvers';
const APPROVER_GROUP_ID = '__approvers_group__';

const Tabs: React.FC<TabsProps> = ({ tabs, defaultTab, onTabChange }) => {
  const mainTabs = tabs.filter(t => !t.section);
  const approverTabs = tabs.filter(t => t.section === APPROVER_SECTION);
  const approverGroupLabel = approverTabs[0]?.sectionLabel || 'Approvers Only';

  const firstMainId = mainTabs[0]?.id || '';
  const firstApproverId = approverTabs[0]?.id || '';

  const resolveDefault = (id?: string) => {
    if (!id) return firstMainId;
    if (approverTabs.find(t => t.id === id)) return id;
    if (mainTabs.find(t => t.id === id)) return id;
    return firstMainId;
  };

  const [activeTab, setActiveTab] = useState(() => resolveDefault(defaultTab));
  const [activeSubTab, setActiveSubTab] = useState(firstApproverId);

  const isApproverActive = approverTabs.some(t => t.id === activeTab);

  useEffect(() => {
    if (defaultTab && defaultTab !== activeTab) {
      const resolved = resolveDefault(defaultTab);
      setActiveTab(resolved);
      if (approverTabs.find(t => t.id === resolved)) {
        setActiveSubTab(resolved);
      }
    }
  }, [defaultTab]);

  const handleMainTabClick = (tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const handleGroupClick = () => {
    const target = activeSubTab || firstApproverId;
    setActiveTab(target);
    onTabChange?.(target);
  };

  const handleSubTabClick = (tabId: string) => {
    setActiveSubTab(tabId);
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const activeTabData = tabs.find(t => t.id === activeTab);

  return (
    <div>
      <div className="nav-tabs">
        {mainTabs.map(tab => (
          <button
            key={tab.id}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleMainTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        {approverTabs.length > 0 && (
          <button
            className={`nav-tab nav-tab--group ${isApproverActive ? 'active' : ''}`}
            onClick={handleGroupClick}
          >
            {approverGroupLabel} <span className="nav-tab-arrow">{isApproverActive ? '▲' : '▼'}</span>
          </button>
        )}
      </div>
      {isApproverActive && (
        <div className="nav-subtabs">
          {approverTabs.map(tab => (
            <button
              key={tab.id}
              className={`nav-subtab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleSubTabClick(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div className="tab-content">
        {activeTabData?.content}
      </div>
    </div>
  );
};

export default Tabs;
